import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

export interface UseConfirmBeforeLeaveOptions {
  hasUnsavedChanges?: boolean;
  onLeave?: () => void;
  onCancel?: () => void;
}

export interface UseConfirmBeforeLeaveReturn {
  showDialog: boolean;
  confirmLeave: () => void;
  cancelLeave: () => void;
  handleBlockedNavigation: (location: { pathname: string; search?: string; hash?: string }) => boolean;
}

/**
 * Intercepts page leave events and shows a confirmation dialog when there are unsaved changes.
 * Handles both browser navigation (tab close, reload) and React Router navigation.
 */
function useConfirmBeforeLeave({ hasUnsavedChanges = false, onLeave, onCancel }: UseConfirmBeforeLeaveOptions = {}): UseConfirmBeforeLeaveReturn {
  const [showDialog, setShowDialog] = useState(false);
  const [nextLocation, setNextLocation] = useState<string | null>(null);
  const history = useHistory();

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();

      // This is required to prevent the browser from closing the tab
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const handleBlockedNavigation = (location: { pathname: string; search?: string; hash?: string }) => {
    if (hasUnsavedChanges && !showDialog) {
      setNextLocation(location.pathname);
      setShowDialog(true);
      return false;
    }
    return true;
  };

  const confirmLeave = () => {
    setShowDialog(false);
    onLeave?.();

    if (nextLocation) {
      setTimeout(() => {
        history.push(nextLocation);
        setNextLocation(null);
      }, 0);
    }
  };

  const cancelLeave = () => {
    setShowDialog(false);
    setNextLocation(null);
    onCancel?.();
  };

  return {
    showDialog,
    confirmLeave,
    cancelLeave,
    handleBlockedNavigation,
  };
}

export default useConfirmBeforeLeave;
